import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowRight, Leaf, Droplets, Brain, HeartPulse, Activity, Sparkles } from "lucide-react";

const pillars = [
  { icon: Leaf, title: "Diet & nutrition" },
  { icon: Droplets, title: "Digestion & excretion" },
  { icon: Brain, title: "Mindset & stress" },
  { icon: HeartPulse, title: "Nervous system & vagal tone" },
  { icon: Activity, title: "Thoughtful testing" },
  { icon: Sparkles, title: "Minimal supplementation" },
];

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 fade-up">
            <p className="overline mb-8" data-testid="hero-eyebrow">
              For functional-medicine-trained practitioners
            </p>
            <h1
              className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
              data-testid="hero-heading"
            >
              Is this mentorship
              <br />
              <em className="italic text-[hsl(var(--gold))]">the right room</em>
              <br />
              for you?
            </h1>
            <p
              className="mt-10 max-w-xl text-lg sm:text-xl leading-relaxed text-[hsl(var(--plum-soft))] font-light"
              data-testid="hero-subheading"
            >
              A short, honest quiz for practitioners who'd rather simplify complex
              cases than pile on more labs, more supplements, and more protocols.
              Nine questions. A clear answer.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-6">
              <Link to="/quiz" className="btn-primary" data-testid="hero-start-quiz-button">
                Start the quiz <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a href="#philosophy" className="overline hover:text-[hsl(var(--gold))] transition-colors" data-testid="hero-scroll-link">
                Read the philosophy ↓
              </a>
            </div>
            <p className="mt-10 text-sm text-[hsl(var(--plum-soft))]">
              Takes about 3 minutes · No sales calls · Your answers stay private
            </p>
          </div>

          <div className="lg:col-span-5 relative" data-testid="hero-media-col">
            <div className="relative aspect-[3/4] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/17570403/pexels-photo-17570403.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700"
                alt="Serene practice space"
                className="w-full h-full object-cover"
                style={{ filter: "sepia(0.15) saturate(0.9)" }}
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(180deg, transparent 40%, hsl(var(--ivory)/0.55) 100%)" }}
              />
            </div>
            <div
              className="absolute -bottom-10 -left-10 hidden lg:block p-8"
              style={{ background: "hsl(var(--ivory))", border: "1px solid hsl(var(--taupe))" }}
            >
              <p className="font-serif text-3xl leading-tight max-w-[16ch]">
                Stay simple, even when the case isn't.
              </p>
              <p className="overline mt-4">Our one rule</p>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section id="philosophy" className="py-24 sm:py-32 rule-top" data-testid="philosophy-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <p className="overline">The approach</p>
            <h2 className="font-serif text-4xl sm:text-5xl mt-6 leading-[1.05]">
              Fewer moving parts.
              <br />
              <em className="italic text-[hsl(var(--sage))]">Deeper</em> results.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg leading-relaxed text-[hsl(var(--plum-soft))] font-light">
            <p>
              Most functional-medicine practices drift toward complexity —
              longer intakes, costlier panels, taller supplement stacks. It
              feels rigorous. It rarely is.
            </p>
            <p className="text-[hsl(var(--plum))]">
              We teach the opposite: a foundations-first framework that stays
              calm and simple, even when the case isn't. Diet. Digestion.
              Excretion pathways. Mindset. Stress. Nervous system and vagal
              tone. Testing and supplements used only when they'll change the
              plan.
            </p>
            <p>
              If you're looking for more protocol templates or a deeper dive
              into costly testing, this isn't that mentorship — and the quiz
              will say so, kindly. The foundational classes are a better
              on-ramp, and they're still part of the family here.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 sm:py-32 rule-top" data-testid="pillars-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-10">
          <div className="flex items-end justify-between mb-16 gap-8 flex-wrap">
            <div>
              <p className="overline">What we build on</p>
              <h2 className="font-serif text-4xl sm:text-5xl mt-6">Six quiet foundations.</h2>
            </div>
            <p className="max-w-md text-[hsl(var(--plum-soft))]">
              Not a protocol. Not a checklist. A way of thinking that holds up
              whether you're seeing your third client or your three-thousandth.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "hsl(var(--taupe))" }}>
            {pillars.map(({ icon: Icon, title }, i) => (
              <div
                key={title}
                data-testid={`pillar-${i}`}
                className="p-10 sm:p-12 flex flex-col gap-8"
                style={{ background: "hsl(var(--ivory))" }}
              >
                <Icon size={28} strokeWidth={1.25} color="hsl(var(--gold))" />
                <h3 className="font-serif text-3xl leading-tight">{title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-24 sm:py-32 rule-top" data-testid="cta-section">
        <div className="mx-auto max-w-5xl px-6 sm:px-10 text-center">
          <h2 className="font-serif text-4xl sm:text-6xl leading-[1.05]">
            Three minutes.
            <br />
            <em className="italic">An honest answer.</em>
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-lg text-[hsl(var(--plum-soft))]">
            Nine gentle questions to see whether the way we practice matches
            the way you want to practice. No pressure — either outcome is a
            welcome one.
          </p>
          <div className="mt-12">
            <Link to="/quiz" className="btn-primary" data-testid="cta-start-quiz-button">
              Begin the quiz <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
